import { AxiosResponse } from 'axios';
import { flatten } from 'lodash-es';
import { useContext } from 'react';
import { useMutation } from 'react-query';
import { addLineItem } from 'utils/api/cartApis';
import { showFlash } from 'utils/domHelpers';
import { AxiosErrorResponse } from 'utils/types';
import { LineItem } from 'utils/types/orderTypes';

import CartContext from './Cart.context';

type UseCartOptions = {
  addItemToCart?: {
    onSuccess?: (
      response: AxiosResponse<LineItem>,
    ) => Promise<boolean> | boolean;
    onError?: (error: AxiosErrorResponse) => Promise<boolean> | boolean;
  };
};

export const useCart = (options?: UseCartOptions) => {
  const {
    order,
    openCart,
    closeCart,
    isCartOpen,
    isCartRefactorEnabled,
    refetchOrder,
    isFetchingOrder,
    cartErrors: errors,
    setCartErrors,
    resetOrder,
  } = useContext(CartContext);

  const {
    mutate: _addItemToCart,
    mutateAsync: _addItemToCartAsync,
    isLoading: isAddItemToCartLoading,
    error: addItemToCartError,
  } = useMutation(addLineItem, {
    onSuccess: async (response) => {
      await refetchOrder?.();

      /* allow custom onSuccess method "preventDefault" */
      const shouldContinue =
        (await options?.addItemToCart?.onSuccess?.(response)) ?? true;
      if (!shouldContinue) return;

      openCart();
    },
    onError: async (error: AxiosErrorResponse) => {
      /* allow custom onError method "preventDefault" */
      const shouldContinue =
        (await options?.addItemToCart?.onError?.(error)) ?? true;
      if (!shouldContinue) return;

      const errors = flatten([error.response?.data?.errors || []]);
      setCartErrors(errors);
      if (error.response?.status === 404) {
        showFlash(
          'alert-error',
          'Failed to locate your previous order. Sorry for the inconvenience!',
        );
        resetOrder();
      } else {
        openCart();
      }
    },
  });

  const addItemToCart = (lineItem: {
    variant_id: number;
    quantity: number;
  }) => {
    _addItemToCart({
      lineItem,
      orderId: order?.id || -1,
    });
  };

  const addItemToCartAsync = async (lineItem: {
    variant_id: number;
    quantity: number;
  }) => {
    try {
      await _addItemToCartAsync({
        lineItem,
        orderId: order?.id || -1,
      });
    } catch (e) {}
  };

  return {
    openCart,
    closeCart,
    isCartOpen,
    isCartRefactorEnabled,

    order,
    refetchOrder,
    isFetchingOrder,
    resetOrder,

    addItemToCart,
    addItemToCartAsync,
    isAddItemToCartLoading,
    addItemToCartError,
    /**
     * @deprecated please don't use this
     */
    errors,
  };
};

export default useCart;
